import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import { memo, useEffect, useState } from 'react';
import { type NetworkProgress, checkUndefinedNullOrEmpty, checkUndefinedOrNull } from './Tools';

let timeoutWarning;
let timeoutError;

type LoadingProps = {
    children?: any;
    color?: 'primary' | 'secondary';
    dialog?: boolean;
    linearProgress?: boolean;
    progress?: NetworkProgress;
    setTimedOut?: (state: boolean) => void;
    timeout?: {
        error?: string;
        timeoutError?: number;
        timeoutWarning?: number;
        warning?: string;
    };
};

const Loading = (props: LoadingProps) => {
    const { children, color = 'primary', dialog, linearProgress, progress, setTimedOut, timeout } = props;
    const [status, setStatus] = useState<'loading' | 'success' | 'warning' | 'error'>('loading');

    useEffect(
        function () {
            if (!checkUndefinedNullOrEmpty(timeout) && progress?.progress < 100) {
                setStatus('loading');
                if (!checkUndefinedNullOrEmpty(timeout?.timeoutWarning) && !checkUndefinedNullOrEmpty(timeout?.warning) && !['error', 'warning'].includes(status)) {
                    timeoutWarning = setTimeout(() => {
                        setStatus('warning');
                        clearTimeout(timeoutWarning);
                    }, timeout?.timeoutWarning);
                }

                if (!checkUndefinedNullOrEmpty(timeout?.timeoutError) && !checkUndefinedNullOrEmpty(timeout?.error) && status !== 'error') {
                    timeoutError = setTimeout(() => {
                        setStatus('error');
                        if (!checkUndefinedOrNull(setTimedOut)) {
                            setTimedOut(true);
                        }
                        clearTimeout(timeoutError);
                    }, timeout?.timeoutError);
                }
            }

            return function cleanup() {
                clearTimeout(timeoutWarning);
                clearTimeout(timeoutError);
            };
        },
        [progress?.progress, setTimedOut, status, timeout]
    );

    const showText = status === 'error' ? timeout?.error : status === 'warning' ? timeout?.warning : children;
    const val = status === 'error' ? 100 : progress?.progress * 100;

    const loadingAnimation = (
        <div style={{ minWidth: 300, textAlign: 'center' }}>
            {linearProgress ? (
                <LinearProgress
                    color={status === 'loading' ? 'primary' : status}
                    value={['error', 'success'].includes(status) ? 100 : progress?.progress * 100}
                    variant={!checkUndefinedNullOrEmpty(val) && val > 0 ? 'determinate' : 'indeterminate'}
                />
            ) : (
                <CircularProgress color={status === 'loading' ? 'primary' : status} size={50} />
            )}
            {!checkUndefinedNullOrEmpty(showText) && (
                <Typography style={{ color: color === 'secondary' ? '#fff' : undefined, marginTop: 8, whiteSpace: 'pre-wrap' }} variant="body1">
                    {showText}
                </Typography>
            )}
        </div>
    );

    return dialog ? (
        <Dialog open fullWidth maxWidth="sm">
            <DialogContent style={{ padding: 20 }}>{loadingAnimation}</DialogContent>
        </Dialog>
    ) : (
        loadingAnimation
    );
};
export default memo(Loading);
